import { AuthErrorType } from '../types/auth';

/**
 * Dependency-free auth-error fixtures (CURI-334). This module maps NextAuth's
 * native error codes onto our `AuthErrorType` union and provides the render
 * metadata (icon + copy + primary CTA) for each type.
 *
 * It intentionally imports NOTHING but the `AuthErrorType` type — no next-auth,
 * React, styled-components, or asset imports — so both the server
 * (`pages/api/auth/[...nextauth].ts`) and the client (the error page, AuthForm)
 * can use it without dragging server-only deps into the browser bundle. This
 * mirrors the `utils/newAuthProviderIds.ts` split. Icon resolution (iconKey →
 * SVG component) lives in the view layer (`AuthErrorView`).
 */

export type AuthErrorAction = 'retry-login' | 'go-signup' | 'go-home' | 'contact-support';

export type AuthErrorIconKey = 'starStruck' | 'foldedHands' | 'appreciate' | 'mail';

export interface AuthErrorMeta {
  iconKey: AuthErrorIconKey;
  /** Tolgee key + English default for the heading. */
  titleKey: string;
  titleDefault: string;
  /** Tolgee key + English default for the body copy. */
  bodyKey: string;
  bodyDefault: string;
  /** Tolgee key + English default for the primary CTA button. */
  ctaKey: string;
  ctaDefault: string;
  /** What the primary CTA does. A "Contact support" link is always also shown. */
  action: AuthErrorAction;
}

/**
 * Map a NextAuth error code (the `?error=` query value NextAuth appends when an
 * OAuth sign-in fails) to our `AuthErrorType`. Also handles our own
 * `RefreshAccessTokenError` sentinel. Unknown OAuth-ish codes degrade to
 * `oauthCallbackError`; the literal `Default` (and empty input) → `unknown`.
 */
export function mapNextAuthCodeToType(code: string | undefined): AuthErrorType {
  switch (code) {
    case 'OAuthAccountNotLinked':
      return 'accountAlreadyExists';
    case 'AccessDenied':
      return 'accessDenied';
    case 'Verification':
    case 'EmailSignin':
    case 'CredentialsSignin':
      return 'magicLinkInvalid';
    case 'SessionRequired':
    case 'RefreshAccessTokenError':
      return 'sessionExpired';
    case 'OAuthSignin':
    case 'OAuthCallback':
    case 'OAuthCreateAccount':
    case 'EmailCreateAccount':
    case 'Callback':
    case 'Configuration':
      return 'oauthCallbackError';
    case undefined:
    case '':
    case 'Default':
      return 'unknown';
    default:
      return 'oauthCallbackError';
  }
}

const ssoMeta = (provider: string): AuthErrorMeta => ({
  iconKey: 'appreciate',
  titleKey: `auth.err.${provider}SSOTitle`,
  titleDefault: `We couldn't sign you in with ${provider}`,
  bodyKey: `auth.err.${provider}SSOBody`,
  bodyDefault: `Is ${provider} SSO set up with Curipod for your school or district? You can also create a free account.`,
  ctaKey: 'auth.err.createAccountCta',
  ctaDefault: 'Create an account for free',
  action: 'go-signup',
});

export const AUTH_ERROR_META: Record<AuthErrorType, AuthErrorMeta> = {
  accountAlreadyExists: {
    iconKey: 'starStruck',
    titleKey: 'auth.err.accountExistsTitle',
    titleDefault: 'Oops! You already signed up.',
    bodyKey: 'auth.err.accountExistsBody',
    bodyDefault:
      'Great to see you again! An account already exists with this email address — go ahead and log in.',
    ctaKey: 'auth.err.logInCta',
    ctaDefault: 'Log in',
    action: 'retry-login',
  },
  doesNotMatchRules: {
    iconKey: 'foldedHands',
    titleKey: 'auth.err.domainTitle',
    titleDefault: "We can't find your email domain",
    bodyKey: 'auth.err.domainBody',
    bodyDefault:
      'You may have used a different email address to sign up. Please try again with another email.',
    ctaKey: 'auth.err.tryAgainCta',
    ctaDefault: 'Try again',
    action: 'retry-login',
  },
  noAccountFound: {
    iconKey: 'appreciate',
    titleKey: 'auth.err.noAccountTitle',
    titleDefault: "We can't find an account for you",
    bodyKey: 'auth.err.noAccountBody',
    bodyDefault:
      'If your school has purchased Curipod, look for an email invite from your admin. Otherwise, you can create a new account now.',
    ctaKey: 'auth.err.signUpNowCta',
    ctaDefault: 'Sign up now',
    action: 'go-signup',
  },
  geolocationFirewall: {
    iconKey: 'appreciate',
    titleKey: 'auth.err.firewallTitle',
    titleDefault: 'Your network is blocking access to Curipod',
    bodyKey: 'auth.err.firewallBody',
    bodyDefault:
      'Your school or workplace network is preventing access to Curipod. Ask your IT administrator to allow access, then try again.',
    ctaKey: 'auth.err.tryAgainCta',
    ctaDefault: 'Try again',
    action: 'retry-login',
  },
  classlinkSSOError: ssoMeta('ClassLink'),
  feideSSOError: ssoMeta('Feide'),
  cleverSSOError: ssoMeta('Clever'),
  googleSSOError: ssoMeta('Google'),
  invalidResponseReceived: {
    iconKey: 'foldedHands',
    titleKey: 'auth.err.unexpectedTitle',
    titleDefault: 'Oh no! An unexpected error occurred.',
    bodyKey: 'auth.err.unexpectedBody',
    bodyDefault:
      "We're very sorry. Our team has been notified. Please try again in a moment.",
    ctaKey: 'auth.err.tryAgainCta',
    ctaDefault: 'Try again',
    action: 'retry-login',
  },
  oauthCallbackError: {
    iconKey: 'foldedHands',
    titleKey: 'auth.err.oauthCallbackTitle',
    titleDefault: "We couldn't complete your sign-in",
    bodyKey: 'auth.err.oauthCallbackBody',
    bodyDefault:
      'Something went wrong while signing you in. Please try again — if it keeps happening, contact support.',
    ctaKey: 'auth.err.tryAgainCta',
    ctaDefault: 'Try again',
    action: 'retry-login',
  },
  accessDenied: {
    iconKey: 'foldedHands',
    titleKey: 'auth.err.accessDeniedTitle',
    titleDefault: 'Sign-in was cancelled',
    bodyKey: 'auth.err.accessDeniedBody',
    bodyDefault:
      'It looks like access was denied before sign-in could finish. Please try again.',
    ctaKey: 'auth.err.tryAgainCta',
    ctaDefault: 'Try again',
    action: 'retry-login',
  },
  sessionExpired: {
    iconKey: 'appreciate',
    titleKey: 'auth.err.sessionExpiredTitle',
    titleDefault: 'Your session has expired',
    bodyKey: 'auth.err.sessionExpiredBody',
    bodyDefault: 'For your security, please sign in again to continue.',
    ctaKey: 'auth.err.logInCta',
    ctaDefault: 'Log in',
    action: 'retry-login',
  },
  magicLinkInvalid: {
    iconKey: 'mail',
    titleKey: 'auth.err.codeInvalidTitle',
    titleDefault: "That code didn't work",
    bodyKey: 'auth.err.codeInvalidBody',
    bodyDefault:
      'The code may be wrong or expired. Head back to log in and request a new code.',
    ctaKey: 'auth.err.backToLoginCta',
    ctaDefault: 'Back to log in',
    action: 'retry-login',
  },
  rateLimited: {
    iconKey: 'foldedHands',
    titleKey: 'auth.err.rateLimitedTitle',
    titleDefault: 'Too many attempts',
    bodyKey: 'auth.err.rateLimitedBody',
    bodyDefault: 'Please wait a minute, then try again.',
    ctaKey: 'auth.err.tryAgainCta',
    ctaDefault: 'Try again',
    action: 'retry-login',
  },
  emailSendFailed: {
    iconKey: 'mail',
    titleKey: 'auth.err.sendFailedTitle',
    titleDefault: "We couldn't send your code",
    bodyKey: 'auth.err.sendFailedBody',
    bodyDefault: 'Something went wrong sending your sign-in code. Please try again.',
    ctaKey: 'auth.err.tryAgainCta',
    ctaDefault: 'Try again',
    action: 'retry-login',
  },
  unknown: {
    iconKey: 'foldedHands',
    titleKey: 'auth.err.unexpectedTitle',
    titleDefault: 'Oh no! An unexpected error occurred.',
    bodyKey: 'auth.err.unexpectedBody',
    bodyDefault:
      "We're very sorry. Our team has been notified. Please try again in a moment.",
    ctaKey: 'auth.err.tryAgainCta',
    ctaDefault: 'Try again',
    // "Try again" must return to sign-in, not home.
    action: 'retry-login',
  },
};
